const libPictView = require('pict-view');

// Browse loads this many materials at a time; "Load more" pulls the next batch. Kept modest because each batch
// is one template pass and a set can transitively reach thousands of materials (2016 Specifications → 2000+).
const BROWSE_BATCH = 250;
// Meadow filter list length guard — chunk the INN lookups so the FilteredTo URL never gets huge.
const INN_CHUNK = 150;
// Server search pulls up to this many global name/code matches, then keeps the ones that belong to THIS set.
// Generous so a set's matches are rarely missed; if the global result hits this, we hint the user to refine.
const SEARCH_LIMIT = 500;
// Don't fire a server search until the term is at least this long — a 1-char LK matches most of the table.
const SEARCH_MIN_CHARS = 2;
// Offer the search box once the set has more than a handful of materials.
const SEARCH_THRESHOLD = 10;
// Debounce keystrokes into one query.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * SpecSetMaterialsTab — a read-only "Materials" tab for a Test Specification Set's read view.
 *
 * Materials are not linked to a SET directly; they link to individual Test Specifications
 * (TestSpecificationMaterialJoin), and each spec belongs to a set (TestSpecification.IDTestSpecificationSet).
 * So "the materials in this set" is a 2-hop:  set → its specs → the materials joined to those specs
 * (de-duplicated). This view resolves the current set id the same way ChildListTab does — from
 * AppData.TestSpecificationSetDetails, the record the read view stashes. Wired in as a { Type: 'View' } read
 * tab (see MasterList-Builder / Headlight-MasterLists).
 *
 * A set can reach thousands of materials, so we don't render them all: the 2-hop resolves the full DISTINCT
 * member-id list once (held in memory), and the tab renders a BROWSE batch (first BROWSE_BATCH, "Load more"
 * for the next). The search box is SERVER-side over the whole set — it queries the Materials table by
 * name/code and keeps only the ids that belong to this set — so any material is findable even when it sits
 * past the loaded browse batch. Browse and search render into two sibling regions (#ssm-browse / #ssm-results)
 * BELOW the search input, so the input is never re-rendered and keeps focus while typing.
 */
class SpecSetMaterialsTab extends libPictView
{
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get destination()
	{
		return this.options.DefaultDestinationAddress || `#PSRS-Tab-Materials`;
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)));
		});
	}

	_setID()
	{
		const tmpSet = this.pict.AppData.TestSpecificationSetDetails || {};
		return tmpSet.IDTestSpecificationSet;
	}

	_escapeHTML(pValue)
	{
		return String((pValue === null || typeof pValue === 'undefined') ? '' : pValue)
			.replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[pChar]);
	}

	_chunk(pArray, pSize)
	{
		const tmpChunks = [];
		for (let i = 0; i < pArray.length; i += pSize) { tmpChunks.push(pArray.slice(i, i + pSize)); }
		return tmpChunks;
	}

	_parse(pHash, pRecord)
	{
		return this.pict.parseTemplateByHash(pHash, pRecord || {});
	}

	// --- Data: resolve the set's distinct member material ids (2-hop), then hydrate ids on demand ---

	/**
	 * Resolve the DISTINCT material ids reachable from this set's specs. Returns a numeric id array (no
	 * hydration — just the ids, so we know the full membership without pulling thousands of rows).
	 */
	async _loadMemberIDs(pSetID)
	{
		// Hop 1: the set's Test Specifications — paged to exhaustion (like hop 2's chunking) so a large spec book that
		// exceeds one page doesn't silently truncate the membership that hops 2/3 and the "exact" scoped search rely on.
		const tmpSpecIDs = [];
		const tmpSpecPageSize = 2000;
		for (let tmpBegin = 0; ; tmpBegin += tmpSpecPageSize)
		{
			const tmpSpecs = await this._getJSON(`TestSpecifications/FilteredTo/FBV~IDTestSpecificationSet~EQ~${ pSetID }/${ tmpBegin }/${ tmpSpecPageSize }`);
			const tmpSpecRows = Array.isArray(tmpSpecs) ? tmpSpecs : [];
			tmpSpecRows.forEach((pSpec) => { if (pSpec.IDTestSpecification > 0) { tmpSpecIDs.push(pSpec.IDTestSpecification); } });
			if (tmpSpecRows.length < tmpSpecPageSize) { break; }   // last (short) page
		}
		if (tmpSpecIDs.length < 1) { return []; }

		// Hop 2: the material joins for those specs (chunked INN), de-duplicated to distinct material ids.
		const tmpMaterialIDSet = {};
		for (const tmpChunk of this._chunk(tmpSpecIDs, INN_CHUNK))
		{
			const tmpJoins = await this._getJSON(`TestSpecificationMaterialJoins/FilteredTo/FBL~IDTestSpecification~INN~${ tmpChunk.join(',') }/0/5000`);
			(Array.isArray(tmpJoins) ? tmpJoins : []).forEach((pJoin) => { if (pJoin.IDMaterial > 0) { tmpMaterialIDSet[pJoin.IDMaterial] = true; } });
		}
		return Object.keys(tmpMaterialIDSet).map((pID) => Number(pID));
	}

	/**
	 * Hydrate a list of material ids into records (chunked INN so the URL never gets huge), each chunk sorted
	 * by name.
	 */
	async _hydrate(pIDs)
	{
		const tmpRows = [];
		for (const tmpChunk of this._chunk(pIDs, INN_CHUNK))
		{
			const tmpBatch = await this._getJSON(`Materials/FilteredTo/FBL~IDMaterial~INN~${ tmpChunk.join(',') }~FSF~Name~ASC~0/0/${ INN_CHUNK }`);
			(Array.isArray(tmpBatch) ? tmpBatch : []).forEach((pRow) => tmpRows.push(pRow));
		}
		return tmpRows;
	}

	// --- Row / region HTML builders ---

	_rowRecord(pMaterial)
	{
		return {
			Name: this._escapeHTML(pMaterial.Name || '—'),
			Code: this._escapeHTML(pMaterial.MaterialCode || pMaterial.CatalogCode || '—'),
			Type: this._escapeHTML(pMaterial.Type || '—'),
		};
	}

	// Just the <tr> rows for a set of materials — used to APPEND a freshly-loaded browse batch to the tbody.
	_rowsHTML(pMaterials)
	{
		return this._parse('SpecSetMaterials-Rows', { Rows: pMaterials.map((pMaterial) => this._rowRecord(pMaterial)) });
	}

	// The scrollable table for a set of materials (pBodyID lets the browse tbody be targeted for appends).
	_tableHTML(pMaterials, pBodyID)
	{
		return this._parse('SpecSetMaterials-Table', { Rows: pMaterials.map((pMaterial) => this._rowRecord(pMaterial)), BodyID: pBodyID || '' });
	}

	// The browse footer: an honest "Showing X of Y" (X = rows actually shown, never the id count — some ids can
	// be stale) plus a "Load more" button while ids remain.
	_browseFootHTML()
	{
		const tmpMore = (this._browseCursor < this._total);
		const tmpNote = tmpMore
			? `Showing ${ this._browseShown } of ${ this._total } materials.`
			: `Showing all ${ this._browseShown } material${ (this._browseShown === 1) ? '' : 's' }.`;
		const tmpRemaining = Math.max(0, this._total - this._browseCursor);
		return this._parse('SpecSetMaterials-BrowseFoot',
			{
				Note: tmpNote,
				MoreSlot: tmpMore ? [ { MoreLabel: `Load ${ Math.min(BROWSE_BATCH, tmpRemaining) } more` } ] : [],
			});
	}

	_renderBrowseRegion(pMaterials)
	{
		const tmpHTML = this._tableHTML(pMaterials, 'ssm-browse-body') + `<div id="ssm-browse-foot">${ this._browseFootHTML() }</div>`;
		this.pict.ContentAssignment.assignContent('#ssm-browse', tmpHTML);
	}

	// --- Render entry point: load membership, paint the container + first browse batch ---

	_resetState()
	{
		clearTimeout(this._searchTimer);
		this._memberIDs = [];
		this._memberIDSet = new Set();
		this._total = 0;
		this._browseCursor = 0; // ids consumed from _memberIDs (the browse offset)
		this._browseShown = 0; // rows actually rendered (≤ cursor; some ids can fail to hydrate)
		this._loadingMore = false;
		this._searchSeq = 0;
	}

	/**
	 * The read view's `View` tab type calls renderAsync() with no arguments. Resolve the set's membership,
	 * paint the container (header + search + empty browse/results regions), then hydrate and show the first
	 * browse batch. Guarded so a failed hop shows a notice, not a blank.
	 */
	async renderAsync()
	{
		this._resetState();
		const tmpSetID = this._setID();
		if (!tmpSetID)
		{
			this.pict.ContentAssignment.assignContent(this.destination, `<div class="ssm-note">Select a specification set to see its materials.</div>`);
			return;
		}
		this.pict.ContentAssignment.assignContent(this.destination, `<div class="ssm-note">Loading materials…</div>`);
		try
		{
			this._memberIDs = await this._loadMemberIDs(tmpSetID);
			this._total = this._memberIDs.length;
			this._memberIDSet = new Set(this._memberIDs);

			const tmpState = this.pict.AppData.SpecSetMaterials = this.pict.AppData.SpecSetMaterials || {};
			tmpState.Count = this._total;
			tmpState.SearchSlot = (this._total > SEARCH_THRESHOLD) ? [ {} ] : [];
			tmpState.EmptySlot = (this._total < 1) ? [ {} ] : [];

			const tmpHTML = this._parse('SpecSetMaterials-Container-Template', {});
			this.pict.ContentAssignment.assignContent(this.destination, tmpHTML);

			if (this._total > 0)
			{
				const tmpFirstIDs = this._memberIDs.slice(0, BROWSE_BATCH);
				const tmpMaterials = await this._hydrate(tmpFirstIDs);
				this._browseCursor = tmpFirstIDs.length;
				this._browseShown = tmpMaterials.length;
				this._renderBrowseRegion(tmpMaterials);
			}
			this.pict.CSSMap.injectCSS();
		}
		catch (pError)
		{
			this.pict.log.warn(`SpecSetMaterialsTab: load failed for set ${ tmpSetID }: ${ pError && (pError.message || pError) }`);
			this.pict.ContentAssignment.assignContent(this.destination, `<div class="ssm-note">Could not load the materials for this specification set.</div>`);
		}
	}

	// --- Browse: load the next batch and APPEND it (O(batch), never a full re-parse of the loaded list) ---

	async loadMore()
	{
		if (this._loadingMore) { return; }
		const tmpNextIDs = (this._memberIDs || []).slice(this._browseCursor, this._browseCursor + BROWSE_BATCH);
		if (tmpNextIDs.length < 1) { return; }
		this._loadingMore = true;
		const tmpFoot = document.querySelector('#ssm-browse-foot');
		if (tmpFoot) { tmpFoot.innerHTML = `<div class="ssm-foot"><span class="ssm-foot-note">Loading…</span></div>`; }
		try
		{
			const tmpMaterials = await this._hydrate(tmpNextIDs);
			const tmpBody = document.querySelector('#ssm-browse-body');
			if (tmpBody) { tmpBody.insertAdjacentHTML('beforeend', this._rowsHTML(tmpMaterials)); }
			this._browseCursor += tmpNextIDs.length;
			this._browseShown += tmpMaterials.length;
		}
		catch (pError)
		{
			this.pict.log.warn(`SpecSetMaterialsTab: load-more failed for set ${ this._setID() }: ${ pError && (pError.message || pError) }`);
		}
		this._loadingMore = false;
		const tmpFootAfter = document.querySelector('#ssm-browse-foot');
		if (tmpFootAfter) { tmpFootAfter.innerHTML = this._browseFootHTML(); }
	}

	// --- Search: server-side over the WHOLE set (name/code), exact — never just the loaded browse rows ---

	search(pTerm)
	{
		clearTimeout(this._searchTimer);
		// Strip the structural `~` so a stray tilde can't break the filter stanza; trim edges.
		const tmpTerm = String(pTerm || '').replace(/~/g, '').trim();
		this._searchTimer = setTimeout(() => { this._runSearch(tmpTerm); }, SEARCH_DEBOUNCE_MS);
	}

	async _runSearch(pTerm)
	{
		if (pTerm.length < SEARCH_MIN_CHARS)
		{
			// Too short to query — fall back to the browse view (which is still in the DOM, just hidden).
			this._showBrowse();
			return;
		}
		const tmpSeq = (this._searchSeq = (this._searchSeq || 0) + 1);
		const tmpPending = document.querySelector('#ssm-results');
		if (tmpPending) { tmpPending.innerHTML = `<div class="ssm-note">Searching…</div>`; }
		this._showResults();
		try
		{
			const tmpMatches = await this._searchMembers(pTerm);
			if (tmpSeq !== this._searchSeq) { return; } // a newer keystroke already superseded this one
			this._renderResults(pTerm, tmpMatches);
		}
		catch (pError)
		{
			if (tmpSeq !== this._searchSeq) { return; }
			this.pict.log.warn(`SpecSetMaterialsTab: search failed for “${ pTerm }”: ${ pError && (pError.message || pError) }`);
			const tmpEl = document.querySelector('#ssm-results');
			if (tmpEl) { tmpEl.innerHTML = `<div class="ssm-note">Could not search materials right now.</div>`; }
		}
	}

	// Build the name/code OR-group filter stanza (without a sort). FoxHound FBVOR ORs all prior clauses, so the
	// chain is wrapped in a FOP…FCP group to stay self-contained — it then ANDs cleanly with an INN id scope.
	_searchGroup(pTerm)
	{
		const tmpLike = encodeURIComponent(`%${ pTerm }%`);
		const tmpFields = [ 'Name', 'MaterialCode', 'CatalogCode' ];
		const tmpClauses = tmpFields.map((pField, pIndex) => `${ (pIndex === 0) ? 'FBV' : 'FBVOR' }~${ pField }~LK~${ tmpLike }`).join('~');
		return `FOP~~(~~${ tmpClauses }~FCP~~)~`;
	}

	/**
	 * Find the set's materials matching a name/code term. Fast path: one global query — if it did NOT hit the
	 * cap, every match is present, so intersecting with the set's ids is exact and cheap. Broad terms (global
	 * matches ≥ SEARCH_LIMIT) could hide set-members past the cap, so those fall back to a set-SCOPED search
	 * (the id list chunked into INN filters, each ANDed with the name/code group) — exact, bounded by set size.
	 * Returns the matched material records, name-sorted.
	 */
	async _searchMembers(pTerm)
	{
		const tmpGroup = this._searchGroup(pTerm);
		const tmpGlobal = await this._getJSON(`Materials/FilteredTo/${ tmpGroup }~FSF~Name~ASC~0/0/${ SEARCH_LIMIT }`);
		const tmpGlobalRows = Array.isArray(tmpGlobal) ? tmpGlobal : [];
		if (tmpGlobalRows.length < SEARCH_LIMIT)
		{
			return tmpGlobalRows.filter((pRow) => this._memberIDSet.has(pRow.IDMaterial));
		}
		return this._searchScoped(tmpGroup);
	}

	// Exact set-scoped search: chunk the member ids into INN filters, AND each with the name/code group, union
	// the (deduped) results. Chunks run in parallel; a failed chunk contributes nothing rather than failing all.
	async _searchScoped(pGroup)
	{
		const tmpChunks = this._chunk(this._memberIDs, INN_CHUNK);
		const tmpResults = await Promise.all(tmpChunks.map((pChunk) =>
			this._getJSON(`Materials/FilteredTo/FBL~IDMaterial~INN~${ pChunk.join(',') }~${ pGroup }~FSF~Name~ASC~0/0/${ INN_CHUNK }`).catch(() => [])));
		const tmpSeen = new Set();
		const tmpMatches = [];
		tmpResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pRow) =>
		{
			if (!tmpSeen.has(pRow.IDMaterial)) { tmpSeen.add(pRow.IDMaterial); tmpMatches.push(pRow); }
		}));
		tmpMatches.sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
		return tmpMatches;
	}

	_renderResults(pTerm, pMatches)
	{
		const tmpEl = document.querySelector('#ssm-results');
		if (!tmpEl) { return; }
		const tmpTotal = pMatches.length;
		if (tmpTotal < 1)
		{
			tmpEl.innerHTML = this._parse('SpecSetMaterials-SearchEmpty', { Term: this._escapeHTML(pTerm) });
			return;
		}
		const tmpShown = pMatches.slice(0, BROWSE_BATCH);
		const tmpParts = [ `${ tmpTotal } material${ (tmpTotal === 1) ? '' : 's' } in this set match${ (tmpTotal === 1) ? 'es' : '' } “${ this._escapeHTML(pTerm) }”.` ];
		if (tmpTotal > tmpShown.length) { tmpParts.push(`Showing the first ${ tmpShown.length }.`); }
		tmpEl.innerHTML = this._tableHTML(tmpShown, 'ssm-results-body') + `<div class="ssm-foot"><span class="ssm-foot-note">${ tmpParts.join(' ') }</span></div>`;
	}

	// Toggle the two regions. The search input lives above both and is never re-rendered, so it keeps focus.
	_showResults()
	{
		const tmpBrowse = document.querySelector('#ssm-browse');
		const tmpResults = document.querySelector('#ssm-results');
		if (tmpBrowse) { tmpBrowse.style.display = 'none'; }
		if (tmpResults) { tmpResults.style.display = ''; }
	}

	_showBrowse()
	{
		const tmpResults = document.querySelector('#ssm-results');
		const tmpBrowse = document.querySelector('#ssm-browse');
		if (tmpResults) { tmpResults.style.display = 'none'; tmpResults.innerHTML = ''; }
		if (tmpBrowse) { tmpBrowse.style.display = ''; }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

const _DEFAULT_CONFIGURATION =
{
	ViewIdentifier: 'SpecSetMaterials',
	DefaultDestinationAddress: '#PSRS-Tab-Materials',
	DefaultRenderable: 'SpecSetMaterials-Container',

	CSS: /*css*/`
.ssm-wrap { padding: 0.25rem 0; }
.ssm-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-brand-primary, #5650e6); margin-bottom: 0.5rem; }
.ssm-count { display: inline-block; margin-left: 0.4rem; padding: 0.02rem 0.5rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; background: var(--theme-color-background-tertiary, #eef1f6); color: var(--theme-color-text-secondary, #45505f); }
.ssm-note { font-size: 0.9rem; color: var(--theme-color-text-muted, #6b7686); padding: 0.5rem 0; }
.ssm-search { margin: 0 0 0.6rem; }
.ssm-search-input { width: 100%; box-sizing: border-box; padding: 0.4rem 0.7rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-primary, #1f2733); font: inherit; font-size: 0.88rem; }
.ssm-search-input:focus { outline: none; border-color: var(--theme-color-brand-primary, #5650e6); }
.ssm-scroll { overflow-x: auto; }
.ssm-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.ssm-table th { position: sticky; top: 0; background: var(--theme-color-background-primary, #fff); text-align: left; font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #6b7686); padding: 0.4rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-default, #d7dce3); white-space: nowrap; }
.ssm-table td { padding: 0.45rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f6); color: var(--theme-color-text-primary, #1f2733); }
.ssm-table td.ssm-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; white-space: nowrap; }
.ssm-table tbody tr:hover { background: var(--theme-color-background-tertiary, #f4f6f9); }
.ssm-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.45rem 0 0.15rem; }
.ssm-foot-note { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); }
.ssm-more-btn { display: inline-flex; align-items: center; gap: 0.3rem; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-secondary, #45505f); border-radius: 7px; padding: 0.3rem 0.75rem; font: inherit; font-size: 0.82rem; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all 0.12s ease; }
.ssm-more-btn:hover { border-color: var(--theme-color-brand-primary, #5650e6); color: var(--theme-color-brand-primary, #5650e6); }
`,

	Templates:
	[
		{
			Hash: 'SpecSetMaterials-Container-Template',
			Template: /*html*/`
				<div class="ssm-wrap">
					<div class="ssm-head">Materials <span class="ssm-count">{~D:AppData.SpecSetMaterials.Count~}</span></div>
					{~TS:SpecSetMaterials-Search:AppData.SpecSetMaterials.SearchSlot~}
					{~TS:SpecSetMaterials-Empty:AppData.SpecSetMaterials.EmptySlot~}
					<div id="ssm-browse" class="ssm-region"></div>
					<div id="ssm-results" class="ssm-region" style="display:none"></div>
				</div>`
		},
		{
			// Shown only when the set is more than a handful (SearchSlot). Fires a server search over the WHOLE
			// set (see search()/_searchMembers). The view id is fixed (registered once as 'SpecSetMaterials').
			Hash: 'SpecSetMaterials-Search',
			Template: /*html*/`<div class="ssm-search"><input class="ssm-search-input" type="text" placeholder="Search materials by name or code…" oninput="_Pict.views['SpecSetMaterials'].search(this.value)" /></div>`
		},
		{
			// Record: { Rows, BodyID }. BodyID lets the browse tbody be targeted for "Load more" appends.
			Hash: 'SpecSetMaterials-Table',
			Template: /*html*/`<div class="ssm-scroll"><table class="ssm-table"><thead><tr><th>Material</th><th>Code</th><th>Type</th></tr></thead><tbody id="{~D:Record.BodyID~}">{~TS:SpecSetMaterials-Row:Record.Rows~}</tbody></table></div>`
		},
		{
			// Just the rows — used to append a freshly-loaded browse batch to an existing tbody.
			Hash: 'SpecSetMaterials-Rows',
			Template: /*html*/`{~TS:SpecSetMaterials-Row:Record.Rows~}`
		},
		{
			Hash: 'SpecSetMaterials-Row',
			Template: /*html*/`<tr><td>{~D:Record.Name~}</td><td class="ssm-mono">{~D:Record.Code~}</td><td>{~D:Record.Type~}</td></tr>`
		},
		{
			// Record: { Note, MoreSlot:[{ MoreLabel }] } — the browse footer note + optional "Load more" button.
			Hash: 'SpecSetMaterials-BrowseFoot',
			Template: /*html*/`<div class="ssm-foot"><span class="ssm-foot-note">{~D:Record.Note~}</span>{~TS:SpecSetMaterials-More:Record.MoreSlot~}</div>`
		},
		{
			Hash: 'SpecSetMaterials-More',
			Template: /*html*/`<button type="button" class="ssm-more-btn" onclick="_Pict.views['SpecSetMaterials'].loadMore()">{~D:Record.MoreLabel~}</button>`
		},
		{
			Hash: 'SpecSetMaterials-Empty',
			Template: /*html*/`<div class="ssm-note">No materials are associated with the specifications in this set.</div>`
		},
		{
			Hash: 'SpecSetMaterials-SearchEmpty',
			Template: /*html*/`<div class="ssm-note">No materials in this set match “{~D:Record.Term~}”.</div>`
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'SpecSetMaterials-Container',
			TemplateHash: 'SpecSetMaterials-Container-Template',
			DestinationAddress: '#PSRS-Tab-Materials',
			RenderMethod: 'replace',
		},
	],
};

module.exports = SpecSetMaterialsTab;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
